import React from "react";
import MuiDivider from "@mui/material/Divider";
import type { DividerProps as MuiDividerProps } from "@mui/material/Divider";
import type { BaseAtomProps } from "../../types";

export interface DividerProps extends MuiDividerProps, BaseAtomProps {
  /**
   * Divider orientation
   */
  orientation?: "horizontal" | "vertical";

  /**
   * Spacing variant for mobile-first design
   */
  spacing?: "none" | "small" | "medium" | "large";

  /**
   * Text or content for labeled divider
   */
  text?: string;
}

const DividerComponent: React.FC<DividerProps> = ({
  orientation = "horizontal",
  spacing = "medium",
  text,
  className,
  children,
  ...props
}) => {
  const spacingMap = {
    none: 0,
    small: { xs: 1, x2l: 1.5 },
    medium: { xs: 2, x2l: 3 },
    large: { xs: 3, x2l: 4 },
  };

  const dividerProps: MuiDividerProps = {
    orientation,
    className,
    sx: {
      // Mobile-first spacing
      margin:
        orientation === "horizontal"
          ? `${
              typeof spacingMap[spacing] === "object"
                ? spacingMap[spacing].xs + "rem"
                : spacingMap[spacing] + "rem"
            } 0`
          : `0 ${
              typeof spacingMap[spacing] === "object"
                ? spacingMap[spacing].xs + "rem"
                : spacingMap[spacing] + "rem"
            }`,
      "@media (min-width:900px)": {
        margin:
          orientation === "horizontal"
            ? `${
                typeof spacingMap[spacing] === "object"
                  ? spacingMap[spacing].x2l + "rem"
                  : spacingMap[spacing] + "rem"
              } 0`
            : `0 ${
                typeof spacingMap[spacing] === "object"
                  ? spacingMap[spacing].x2l + "rem"
                  : spacingMap[spacing] + "rem"
              }`,
      },
    },
    ...props,
  };

  // If text is provided, render a labeled divider
  if (text) {
    return <MuiDivider {...dividerProps}>{text}</MuiDivider>;
  }

  // If children are provided, render them inside the divider
  if (children) {
    return <MuiDivider {...dividerProps}>{children}</MuiDivider>;
  }

  // Otherwise render a simple divider
  return <MuiDivider {...dividerProps} />;
};

// Apply memoization with React.memo
export const Divider = React.memo(DividerComponent);

// Set display name for debugging
(Divider as any).displayName = "Divider";
